import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import PromptImprover from '../../PromptImprover';
import { toast } from 'sonner';
import { Sparkles, Loader2, Image as ImageIcon, Maximize2, X } from 'lucide-react';
import { useAuthStore } from '../../../../store/authStore';
import { useConfigStore } from '../../../store/configStore';
import axios from 'axios';
import ImageGallery from './ImageGallery';
import { notifyCreditsCharged } from '../../../lib/credits';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

// Estilos predefinidos para generación de imágenes
const IMAGE_STYLES = [
  { value: 'realistic', label: 'Realista', prompt: 'photorealistic, high quality, detailed' },
  { value: 'artistic', label: 'Artístico', prompt: 'artistic, creative, expressive' },
  { value: 'cartoon', label: 'Caricatura', prompt: 'cartoon style, colorful, fun' },
  { value: 'sketch', label: 'Boceto', prompt: 'pencil sketch, hand-drawn, artistic' },
  { value: 'watercolor', label: 'Acuarela', prompt: 'watercolor painting, soft colors, artistic' },
  { value: 'digital-art', label: 'Arte Digital', prompt: 'digital art, modern, vibrant' },
  { value: 'oil-painting', label: 'Óleo', prompt: 'oil painting, classical, textured' },
  { value: 'minimalist', label: 'Minimalista', prompt: 'minimalist, simple, clean design' },
  { value: 'abstract', label: 'Abstracto', prompt: 'abstract art, creative, unique' },
  { value: '3d-render', label: 'Render 3D', prompt: '3D render, professional, detailed' },
];

const ImageGenerateDialog = ({ open, onOpenChange, onInsertImage }) => {
  const { token } = useAuthStore();
  const { apiKey, imageModel } = useConfigStore();
  const [userPrompt, setUserPrompt] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('realistic');
  const [imageAlt, setImageAlt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  // Canonical /assets path returned by the backend (null for external images);
  // this is what gets inserted into the document, not the signed preview URL.
  const [generatedImagePath, setGeneratedImagePath] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  // --- Imagen de referencia opcional (img2img / edición) ---
  const [referenceFile, setReferenceFile] = useState(null);
  const [referencePreview, setReferencePreview] = useState(null);
  const [referencePath, setReferencePath] = useState(null);
  const [referenceUploading, setReferenceUploading] = useState(false);
  const [galleryMode, setGalleryMode] = useState('insert'); // 'insert' | 'reference'

  // Tipos y tamaño permitidos para la imagen de referencia (igual que ImageUploadDialog)
  const VALID_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

  const handleReferenceSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      toast.error('Formato de imagen no soportado. Usa JPG, PNG, GIF, WebP o SVG.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('La imagen es demasiado grande. Máximo 10MB.');
      e.target.value = '';
      return;
    }

    setReferenceFile(file);
    setReferencePath(null); // fuerza re-subida al generar

    const reader = new FileReader();
    reader.onloadend = () => {
      setReferencePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearReference = () => {
    setReferenceFile(null);
    setReferencePreview(null);
    setReferencePath(null);
  };

  const hasReference = Boolean(referencePath || referencePreview);

  const handleGenerate = async () => {
    if (!userPrompt.trim()) {
      toast.error('Ingresa una descripción para la imagen');
      return;
    }

    // No client-side API-key gate: the backend uses a platform key when the
    // user hasn't configured their own (BYOK). Send the user's key if present.

    setGenerating(true);
    setGeneratedImageUrl(null);

    let refPathForRequest = referencePath;

    try {
      // --- Subir imagen de referencia si la hay y aún no tiene ruta canónica ---
      if (referenceFile && !referencePath) {
        setReferenceUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', referenceFile);

          const uploadRes = await axios.post(
            `${API}/images/upload`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          const canonical = uploadRes.data.path || uploadRes.data.url;
          if (!canonical) {
            throw new Error('No se recibió ruta de la imagen subida');
          }
          setReferencePath(canonical);
          refPathForRequest = canonical;
        } catch (uploadError) {
          console.error('Error uploading reference image:', uploadError);
          toast.error('No se pudo subir la imagen de referencia');
          return; // Aborta: no generes sin la referencia que el usuario puso
        } finally {
          setReferenceUploading(false);
        }
      }

      // Construir el prompt completo con el estilo seleccionado
      const styleConfig = IMAGE_STYLES.find(s => s.value === selectedStyle);
      const fullPrompt = `${userPrompt}, ${styleConfig.prompt}`;

      const response = await axios.post(
        `${API}/ai/generate-image`,
        {
          prompt: fullPrompt,
          // T-031: send the user-configured image model (default is the working
          // image-capable model). Send BYOK only when configured.
          model: imageModel,
          ...(apiKey ? { api_key: apiKey } : {}),
          // Imagen de referencia (img2img/edición). Solo se envía cuando hay ruta canónica.
          ...(refPathForRequest ? { reference_image: refPathForRequest } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const imageUrl = response.data.image_url;
      console.log('Imagen generada:', imageUrl); // Debug
      setGeneratedImageUrl(imageUrl);
      setGeneratedImagePath(response.data.path || null);
      // T-052: refresh the credit badge + show the "−N créditos" toast for the
      // charge this generation incurred (same helper the chat/EditorPage use).
      notifyCreditsCharged(response.data.credits);
      toast.success('Imagen generada exitosamente. Revisa la vista previa abajo.');
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error(error.response?.data?.detail || 'Error al generar imagen');
      setGeneratedImageUrl(null);
      setGeneratedImagePath(null);
    } finally {
      setGenerating(false);
    }
  };

  const handleInsert = () => {
    if (!generatedImageUrl) {
      toast.error('Genera una imagen primero');
      return;
    }

    const altText = imageAlt || userPrompt;
    // Insert the canonical path when the image was stored as a file; fall back
    // to the URL for external images.
    const insertSrc = generatedImagePath || generatedImageUrl;

    // Close dialog first to remove aria-hidden
    handleClose();

    // Then insert image after a small delay
    setTimeout(() => {
      onInsertImage(insertSrc, altText);
      toast.success('Imagen insertada');
    }, 150);
  };

  const handleClose = () => {
    // Si hay una imagen generada, mostrar confirmación
    if (generatedImageUrl) {
      toast.info('Imagen guardada en tu galería. Puedes acceder a ella desde el botón de Galería.');
    }

    // Reset all fields
    setUserPrompt('');
    setImageAlt('');
    setSelectedStyle('realistic');
    setGeneratedImageUrl(null);
    setGeneratedImagePath(null);
    setShowFullImage(false);
    // Reset imagen de referencia
    setReferenceFile(null);
    setReferencePreview(null);
    setReferencePath(null);
    setReferenceUploading(false);
    setGalleryMode('insert');
    onOpenChange(false);
  };

  const handleDialogChange = (isOpen) => {
    if (!isOpen) {
      // When dialog is closed, reset all fields
      handleClose();
    } else {
      onOpenChange(isOpen);
    }
  };

  const handleGallerySelect = (url, altText) => {
    if (galleryMode === 'reference') {
      setReferencePath(url);
      setReferencePreview(url);
      setReferenceFile(null); // vino de galería, no hay File que subir
      setShowGallery(false);
      setGalleryMode('insert');
      toast.success('Imagen de referencia seleccionada');
      return;
    }

    onInsertImage(url, altText);
    setShowGallery(false);
    toast.success('Imagen insertada desde galería');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogChange}>
        <DialogContent className="glass border-0 w-[90vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="image-generate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            Generar Imagen con IA
          </DialogTitle>
          <DialogDescription>
            Describe la imagen que quieres generar y elige un estilo artístico.
            {hasReference && (
              <span className="block mt-1 text-purple-600 dark:text-purple-400">
                ✓ Se usará la imagen de referencia como base para la generación.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Descripción de la imagen */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="image-prompt">Descripción de la Imagen</Label>
              {/* Mejorar con IA la descripción (pedido del dueño); mismo
                  componente por-campo que comandos y prompts de documento. */}
              <PromptImprover
                currentPrompt={userPrompt}
                onImproved={(improved) => setUserPrompt(improved)}
                disabled={generating}
                label="Mejorar descripción"
              />
            </div>
            <Textarea
              id="image-prompt"
              placeholder="Ej: Un gato naranja sentado en una ventana mirando la lluvia"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              disabled={generating}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Imagen de referencia (opcional) - img2img / edición */}
          <div className="space-y-2">
            <Label>Imagen de referencia (opcional)</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleReferenceSelect}
                disabled={generating || referenceUploading}
                className="flex-1 min-w-[200px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setGalleryMode('reference');
                  setShowGallery(true);
                }}
                disabled={generating || referenceUploading}
                className="flex items-center gap-1.5"
              >
                <ImageIcon className="w-4 h-4" />
                Desde galería
              </Button>
            </div>

            {referencePreview && (
              <div className="relative inline-block mt-2">
                <img
                  src={referencePreview}
                  alt="Referencia"
                  className="max-h-32 object-contain rounded-lg border border-slate-300 dark:border-slate-600"
                />
                <button
                  type="button"
                  onClick={clearReference}
                  disabled={generating || referenceUploading}
                  className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 shadow-md transition-colors disabled:opacity-50"
                  aria-label="Quitar imagen de referencia"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            <p className="text-xs text-slate-500 dark:text-slate-400">
              La IA usará esta imagen como base para generar la nueva. Opcional.
            </p>
          </div>

          {/* Selector de estilo */}
          <div className="space-y-2">
            <Label htmlFor="image-style">Estilo Artístico</Label>
            <Select value={selectedStyle} onValueChange={setSelectedStyle} disabled={generating}>
              <SelectTrigger id="image-style">
                <SelectValue placeholder="Selecciona un estilo" />
              </SelectTrigger>
              <SelectContent>
                {IMAGE_STYLES.map((style) => (
                  <SelectItem key={style.value} value={style.value}>
                    {style.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Texto alternativo */}
          <div className="space-y-2">
            <Label htmlFor="alt-text-generate">Texto Alternativo (opcional)</Label>
            <Input
              id="alt-text-generate"
              placeholder="Descripción de la imagen para accesibilidad"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              disabled={generating}
            />
          </div>

          {/* Botón de generar */}
          <Button
            onClick={handleGenerate}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
            disabled={!userPrompt.trim() || generating || referenceUploading}
          >
            {generating || referenceUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {referenceUploading ? 'Subiendo referencia...' : 'Generando imagen...'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {hasReference ? 'Generar con referencia' : 'Generar Imagen'}
              </>
            )}
          </Button>

          {/* Vista previa de la imagen generada */}
          {generatedImageUrl && (
            <div className="space-y-3 border-2 border-green-500 dark:border-green-700 rounded-lg p-4 bg-green-50/50 dark:bg-green-950/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <Label className="text-green-700 dark:text-green-300 font-semibold text-lg">✓ Imagen Generada</Label>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowFullImage(true)}
                  className="flex items-center gap-2 hover:bg-green-100 dark:hover:bg-green-900/40"
                >
                  <Maximize2 className="w-4 h-4" />
                  Ver Completa
                </Button>
              </div>
              <div className="relative border-2 border-green-300 dark:border-green-800 rounded-lg p-2 bg-white dark:bg-slate-800 flex justify-center cursor-pointer hover:border-green-400 dark:hover:border-green-600 transition-colors"
                   onClick={() => setShowFullImage(true)}
                   title="Click para ver en tamaño completo">
                <img
                  src={generatedImageUrl}
                  alt="Generated preview"
                  className="max-h-48 sm:max-h-64 md:max-h-72 w-auto object-contain rounded shadow-lg"
                  onError={(e) => {
                    console.error('Error loading image:', generatedImageUrl);
                    console.error('Image element:', e.target);
                    toast.error('Error al cargar la imagen. Verifica la consola para más detalles.');
                  }}
                  onLoad={() => {
                    console.log('Image loaded successfully:', generatedImageUrl);
                  }}
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-300 text-center">
                La imagen ha sido guardada en tu galería. Puedes insertarla en el documento o cerrar para usarla más tarde.
              </p>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={generating}
            >
              {generatedImageUrl ? 'Guardar Solo en Galería' : 'Cancelar'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setGalleryMode('insert');
                setShowGallery(true);
              }}
              className="flex-1"
              disabled={generating}
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Ver Galería
            </Button>
            {generatedImageUrl && (
              <Button
                onClick={handleInsert}
                className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                disabled={generating}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Insertar en Documento
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Image Gallery Dialog */}
    <ImageGallery
      open={showGallery}
      onOpenChange={setShowGallery}
      onSelectImage={handleGallerySelect}
    />

    {/* Full Size Image Modal */}
    <Dialog open={showFullImage} onOpenChange={setShowFullImage}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden bg-black/95 border-0 flex items-center justify-center">
        <button
          onClick={() => setShowFullImage(false)}
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-6 h-6 text-white" />
        </button>
        <div className="w-full h-full flex items-center justify-center p-8">
          {generatedImageUrl && (
            <img
              src={generatedImageUrl}
              alt="Imagen generada - Vista completa"
              className="max-w-full max-h-[85vh] object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ImageGenerateDialog;

